import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/constants/routes';
import { ProfileCircleIcon, ChevronLeftIcon } from '@/components/icons/BrandIcons';
import { useAuthStore } from '@/features/auth';

/** "22 Jun 2026 | 10:08 AM" from an ISO timestamp, for the "Last login" line. */
function formatLastLogin(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const datePart = date.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${datePart} | ${timePart}`;
}

/**
 * Header account menu (Figma 6/7b dropdown): the profile avatar on the right of
 * the portal nav. Clicking it opens a card showing the signed-in user's name +
 * email, a link to the Profile page, and Log out. Replaces the old header
 * Relationship-Manager entry (which now lives in the dashboard title row).
 */
export function ProfileMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const previousLoginAt = useAuthStore((s) => s.previousLoginAt);
  const logout = useAuthStore((s) => s.logout);
  const lastLogin = previousLoginAt ? formatLastLogin(previousLoginAt) : null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('portalNav.account')}
        className="flex items-center rounded-full transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-barita-mist/60"
      >
        <ProfileCircleIcon className="h-9.25 w-9.25 shrink-0 text-barita-mist" />
      </button>

      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 z-40 mt-3 w-[300px] max-w-[calc(100vw-48px)] rounded-[12px] bg-white p-2 text-barita-ink shadow-xl ring-1 ring-black/5"
          >
            <span
              aria-hidden
              className="pointer-events-none absolute -top-1.5 right-6 h-3 w-3 rotate-45 bg-white"
            />

            <div className="px-3 pb-3 pt-2">
              <p className="truncate text-[15px] font-semibold text-barita-ink">
                {user?.name ?? '—'}
              </p>
              {user?.email && (
                <p className="mt-0.5 truncate text-[13px] text-barita-ink/55">{user.email}</p>
              )}
              {lastLogin && (
                <p className="mt-2 text-[12px] text-barita-ink/45">
                  {t('portalNav.lastLogin', { when: lastLogin })}
                </p>
              )}
            </div>

            <hr className="border-black/5" />

            <Link
              to={ROUTES.profile}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="mt-1 flex items-center justify-between rounded-lg px-3 py-2.5 text-[14px] font-medium text-barita-ink transition hover:bg-barita-mist/40"
            >
              {t('portalNav.goToMyProfile')}
              <ChevronLeftIcon className="h-4 w-4 rotate-180 text-barita-ink/40" />
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                logout();
              }}
              className="flex w-full items-center rounded-lg px-3 py-2.5 text-left text-[14px] font-medium text-error transition hover:bg-error-light"
            >
              {t('auth.logout')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
